import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getFeatureInfo } from '../../../common/getFeatureInfo';
import type { AudioFeature } from '../../../types/audioFeature';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../hooks/useAudioPlayerStreamingStore';
import { AudioStreamingState } from '../../state/AudioPlayerStreamingStore';
import { Playlist } from '../playlist/Playlist';

import { AudioFeedbackFooter } from './AudioFeedbackFooter';
import { AudioHeaderPanel } from './AudioHeaderPanel';
import { AudioPlayerError } from './AudioPlayerError';
import { AudioTimeIndicator } from './AudioTimeIndicator';
import { MiniplayerControls } from './controls/MiniplayerControls';
import { OverflowMenu } from './menu/OverflowMenu';
import { PanelWrapper } from './menu/PanelWrapper';

const i18n = defineMessages({
	closeButtonLabel: {
		id: 'audio.audio-player.close-player-button',
		defaultMessage: 'Close audio player',
		description: 'The label used for the X icon to close the audio player',
	},
});

const styles = cssMap({
	expandedAudioPlayerContainer: {
		width: '380px',
		position: 'relative',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	audioPlayerMinHeight: {
		minHeight: '470px',
	},
	audioPlayerWithPendingContent: {
		height: '532px',
	},
	buttonsContainer: {
		paddingInline: token('space.050'),
	},
	controlsContainer: {
		height: '404px',
	},
});

const backgroundStyle = {
	background: `linear-gradient(180deg, ${token('elevation.surface')} 50%, ${token('elevation.surface.sunken')} 100%)`,
};

type ExpandedAudioPlayerProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	audioFeatures: AudioFeature[];
};

export const ExpandedAudioPlayer = ({ audioRef, audioFeatures }: ExpandedAudioPlayerProps) => {
	const { formatMessage } = useIntl();

	const { activeFeature, cloudId, activeMenu, closeAudioPlayer, pendingContent, setActiveMenu } =
		useAudioPlayerStore();
	const { state: streamingState } = useAudioPlayerStreamingStore();

	const activeFeatureInfo = getFeatureInfo(activeFeature, audioFeatures);

	const hasOverflowMenu =
		activeFeatureInfo?.menuConfig?.items && activeFeatureInfo?.menuConfig?.items.length > 0;

	const isErrorState = streamingState.state === AudioStreamingState.ERROR;

	if (activeMenu) {
		return (
			<Box xcss={styles.expandedAudioPlayerContainer} style={backgroundStyle}>
				<PanelWrapper header={activeMenu.title} onBack={() => setActiveMenu(undefined)}>
					{activeMenu.renderPanel?.body}
				</PanelWrapper>
			</Box>
		);
	}

	return (
		<Box
			xcss={cx(
				styles.expandedAudioPlayerContainer,
				styles.audioPlayerMinHeight,
				pendingContent && styles.audioPlayerWithPendingContent,
			)}
			style={backgroundStyle}
		>
			<Stack spread="space-between" xcss={styles.controlsContainer}>
				<Stack space="space.250" alignInline="center">
					<Inline
						alignInline="end"
						alignBlock="center"
						xcss={styles.buttonsContainer}
						grow="fill"
						spread={hasOverflowMenu ? 'space-between' : undefined}
					>
						{hasOverflowMenu && <OverflowMenu items={activeFeatureInfo?.menuConfig!.items} />}
						<IconButton
							appearance="subtle"
							spacing="compact"
							icon={CrossIcon}
							label={formatMessage(i18n.closeButtonLabel)}
							onClick={closeAudioPlayer}
						/>
					</Inline>
					<AudioHeaderPanel featureList={audioFeatures} />
					{isErrorState ? (
						<Flex justifyContent="center">
							<AudioPlayerError onRetry={() => {}} />
						</Flex>
					) : (
						<>
							<MiniplayerControls audioRef={audioRef} />
							{streamingState.state !== AudioStreamingState.UNINITIALIZED && (
								<AudioTimeIndicator audioRef={audioRef} />
							)}
						</>
					)}
				</Stack>
				<Box>
					<AudioFeedbackFooter />
				</Box>
			</Stack>
			<Playlist audioRef={audioRef} cloudId={cloudId} />
		</Box>
	);
};
